import { type AnimationEvent, ReactNode, useEffect, FC, useRef, useState } from 'react';
import cx from 'classnames';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useAvoidBackgroundScrolling from '@/hooks/useAvoidBackgroundScrolling';
import Text from '../Text/Text';
import { DEFAULT_TITLE_ID } from './AiChatDrawer.constants';
import useDrawerAccessibility from './useDrawerAccessibility';
import useDrawerResize from './useDrawerResize';
import { TestIds } from './testIds';
import styles from './AiChatDrawer.module.css';

export type AiChatDrawerVariant = 'default' | 'fullHeight';

export interface AiChatDrawerProps {
  isOpen: boolean;
  onClose: VoidFunction;
  header?: ReactNode;
  children: ReactNode;
  variant?: AiChatDrawerVariant;
  disableCloseAnimation?: boolean;
}

const AiChatDrawer: FC<AiChatDrawerProps> = ({
  isOpen,
  onClose,
  header,
  children,
  variant = 'default',
  disableCloseAnimation = false,
}) => {
  const { t } = useTranslation();
  const [shouldRender, setShouldRender] = useState(isOpen);
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const { widthRem, isDragging, handleMouseDown, handleKeyDown, minWidthRem, maxWidthRem } = useDrawerResize(isOpen);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setShouldRender(true);

      return;
    }

    if (disableCloseAnimation) {
      setShouldRender(false);
      previousFocusRef.current?.focus();
    }
  }, [disableCloseAnimation, isOpen]);

  const shouldSkipCloseAnimation = !isOpen && disableCloseAnimation;
  const isRendered = shouldRender && !shouldSkipCloseAnimation;

  useAvoidBackgroundScrolling(isRendered);

  const handleDrawerAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || isOpen) {
      return;
    }

    setShouldRender(false);
    previousFocusRef.current?.focus();
  };

  const { handleDrawerKeyDown } = useDrawerAccessibility({ drawerRef, isOpen, shouldRender, onClose });

  if (!isRendered) return null;

  const isClosing = !isOpen && !disableCloseAnimation;
  const drawerStyle = { width: `${widthRem}rem` };

  return createPortal(
    <>
      <div
        className={cx(styles.backdrop, isClosing && styles.closing)}
        onClick={onClose}
        aria-hidden="true"
        data-testid={TestIds.backdrop}
      />
      <div
        ref={drawerRef}
        className={cx(styles.drawer, isClosing && styles.closing, isDragging && styles.dragging)}
        style={drawerStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={DEFAULT_TITLE_ID}
        onAnimationEnd={handleDrawerAnimationEnd}
        onKeyDown={handleDrawerKeyDown}
        data-testid={TestIds.drawer}
      >
        {!header && (
          <Text as="h2" id={DEFAULT_TITLE_ID} className={styles.visuallyHidden}>
            {t('components.aiChatDrawer.accessibleTitle')}
          </Text>
        )}

        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={Math.round(widthRem)}
          aria-valuemin={minWidthRem}
          aria-valuemax={Math.round(maxWidthRem)}
          aria-label={t('components.aiChatDrawer.resizeHandle')}
          tabIndex={0}
          className={styles.resizeHandle}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          data-testid={TestIds.resizeHandle}
        />

        {header && (
          <div id={DEFAULT_TITLE_ID} className={styles.headerSlot} data-testid={TestIds.header}>
            {header}
          </div>
        )}

        {variant === 'fullHeight' ? (
          <div className={styles.fullHeightContent} data-testid={TestIds.content}>
            {children}
          </div>
        ) : (
          <div className={styles.content} data-testid={TestIds.content}>
            <div className={styles.contentInner} data-testid={TestIds.contentInner}>
              {children}
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};

export default AiChatDrawer;
